import { WarningIcon } from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { ErrorBoundary } from '../../../components/error-boundaries/error-boundary.tsx'
import { useAllIntegrations, useGetIntegrationsAllowedMethods } from '../../../utils/api-client/api-hooks.ts'
import type { Integration } from '../../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../../utils/text-utils.ts'
import { DocumentationDrawer } from './documentation-drawer.tsx'
import { InstanceCard, ReadOnlyInstanceCard } from './instance-card.tsx'
import type { CatalogEntry } from './integration-instance-utils.ts'
import { TOOL_INTEGRATION_CATALOG, getInstanceHealth, getIsSystemInstance } from './integration-instance-utils.ts'
import * as styles from './integrations-tab.css.ts'
import { ManageDrawer } from './manage-drawer.tsx'
import { SetupDrawer } from './setup-drawer.tsx'
import { ToolCatalogCard } from './tool-catalog-card.tsx'

/**
 * Every configured instance, across pages, so nothing is capped and no caller guesses a page size.
 * Non-suspense so the badge renders while the request is in flight rather than suspending the page.
 */
// Counts only product-managed instances for the tab badge. System instances are owned by the admin
// console and are not "configured integrations" from the user's perspective.
export const useConfiguredIntegrationCount = (): number => {
  const { integrations } = useAllIntegrations()
  return integrations.filter(integration => !getIsSystemInstance(integration)).length
}

/**
 * Whether this installation opted into in-product integration management.
 *
 * The collection answers an OPTIONS probe with the methods it supports; an installation serving
 * integrations read-only omits POST and answers every write with 405. Composing that into a single
 * boolean belongs here rather than in `api-hooks`, which stays a thin wrapper over the endpoint.
 * A probe that errors is read as enabled: a backend that does not answer OPTIONS at all should not
 * cost the whole tab its affordances, and a write that turns out to be refused surfaces its own 405.
 */
const useIsManagementEnabled = (): { isEnabled: boolean; isPending: boolean } => {
  const { data: allowedMethods, isPending, isError } = useGetIntegrationsAllowedMethods()
  return { isEnabled: isError || (allowedMethods?.includes('POST') ?? false), isPending }
}

// SCM providers are managed elsewhere, so the catalog lists only tools that supply findings.
const CATALOG_GROUPS = [{ heading: 'Scanner Integrations', entries: TOOL_INTEGRATION_CATALOG }] as const

type ActiveDrawer =
  | { kind: 'setup'; entry: CatalogEntry }
  | { kind: 'docs'; entry: CatalogEntry }
  | { kind: 'manage'; integration: Integration }
  | undefined

export const IntegrationsTab: React.FC = () => {
  return (
    <ErrorBoundary fallbackTitle="Failed to load integrations">
      <IntegrationsTabContent />
    </ErrorBoundary>
  )
}

const IntegrationsTabContent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isAvailableHidden = searchParams.get('available') === 'hidden'
  const [activeDrawer, setActiveDrawer] = useState<ActiveDrawer>(undefined)

  const { integrations, isLoading: isLoadingIntegrations } = useAllIntegrations()

  // In-product management is opt-in per install. Where it is off the backend rejects writes, so the
  // tab collapses to a read-only list — no catalog, no create/edit/delete affordances. Wait for the
  // probe before deciding so the tab never flashes the read-only layout while it is still in flight.
  const { isEnabled: isManagementEnabled, isPending: isManagementProbePending } = useIsManagementEnabled()

  const configuredIntegrations = useMemo(
    () => integrations.filter(integration => !getIsSystemInstance(integration)),
    [integrations]
  )

  const needsAttentionCount = configuredIntegrations.filter(
    integration => getInstanceHealth(integration) === 'needs-attention'
  ).length

  const toggleAvailable = () =>
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous)
        if (isAvailableHidden) {
          next.delete('available')
        } else {
          next.set('available', 'hidden')
        }
        return next
      },
      { replace: true }
    )

  // Until the first integration is configured the catalog is the whole point of the tab, so it renders
  // on its own. Once something is configured it moves below an "Available" division that can be hidden.
  const isEmpty = configuredIntegrations.length === 0

  if (isManagementProbePending || isLoadingIntegrations) {
    return <div className={styles.loading}>Loading…</div>
  }

  if (!isManagementEnabled) {
    return <ReadOnlyIntegrationsList integrations={configuredIntegrations} />
  }

  return (
    <div className={styles.container}>
      {needsAttentionCount > 0 && (
        <div className={styles.attentionBanner} role="status">
          <WarningIcon size={20} weight="light" className={styles.attentionBannerIcon} />
          <p className={styles.attentionBannerText}>
            <span className={styles.attentionBannerCount}>{needsAttentionCount}</span>{' '}
            {pluralize(needsAttentionCount, 'integration needs', 'integrations need')} your attention
          </p>
        </div>
      )}

      {isEmpty ? (
        <>
          <div className={styles.emptyStateCard} data-testid="integrations-empty-state">
            <p className={styles.emptyStateTitle}>No integrations</p>
            <p className={styles.emptyStateSubtitle}>Connect a tool to get started</p>
          </div>
          <CatalogGrid entries={CATALOG_GROUPS.flatMap(group => group.entries)} onOpenDrawer={setActiveDrawer} />
        </>
      ) : (
        <>
          <div className={styles.cardGrid}>
            {configuredIntegrations.map(integration => (
              <InstanceCard
                key={integration.id}
                integration={integration}
                onManage={() => setActiveDrawer({ kind: 'manage', integration })}
              />
            ))}
          </div>

          <hr className={styles.divider} />

          <div className={styles.availableHeaderRow}>
            <h2 className={styles.availableHeading}>Available</h2>
            <DefaultButton
              buttonType={isAvailableHidden ? 'primary' : 'secondary'}
              size="small"
              onClick={toggleAvailable}
            >
              {isAvailableHidden ? 'Show available' : 'Hide available'}
            </DefaultButton>
          </div>

          {!isAvailableHidden && (
            <div className={styles.catalogGroups}>
              {CATALOG_GROUPS.map(group => (
                <section key={group.heading}>
                  <h3 className={styles.catalogGroupHeading}>{group.heading}</h3>
                  <CatalogGrid entries={group.entries} onOpenDrawer={setActiveDrawer} />
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {activeDrawer?.kind === 'setup' && (
        <SetupDrawer entry={activeDrawer.entry} onClose={() => setActiveDrawer(undefined)} />
      )}
      {activeDrawer?.kind === 'docs' && (
        <DocumentationDrawer
          entry={activeDrawer.entry}
          onClose={() => setActiveDrawer(undefined)}
          onAddIntegration={() => setActiveDrawer({ kind: 'setup', entry: activeDrawer.entry })}
        />
      )}
      {activeDrawer?.kind === 'manage' && (
        <ManageDrawer integration={activeDrawer.integration} onClose={() => setActiveDrawer(undefined)} />
      )}
    </div>
  )
}

type CatalogGridProps = {
  entries: CatalogEntry[]
  onOpenDrawer: (drawer: ActiveDrawer) => unknown
}

const CatalogGrid: React.FC<CatalogGridProps> = ({ entries, onOpenDrawer }) => (
  <div className={styles.catalogGrid}>
    {entries.map(entry => (
      <ToolCatalogCard
        key={entry.id}
        entry={entry}
        onAdd={() => onOpenDrawer({ kind: 'setup', entry })}
        onViewDocs={() => onOpenDrawer({ kind: 'docs', entry })}
      />
    ))}
  </div>
)

// What the tab shows where in-product integration management is off: name and type only, nothing to
// act on. Scoped to the same instances the managed view and the tab badge count, so the three cannot
// disagree — system instances are owned by the admin console and are out of scope for this tab.
const ReadOnlyIntegrationsList: React.FC<{ integrations: Integration[] }> = ({ integrations }) => (
  <div className={styles.container}>
    {integrations.length === 0 ? (
      <div className={styles.emptyStateCard} data-testid="integrations-empty-state">
        <p className={styles.emptyStateTitle}>No integrations</p>
      </div>
    ) : (
      <div className={styles.cardGrid}>
        {integrations.map(integration => (
          <ReadOnlyInstanceCard key={integration.id} integration={integration} />
        ))}
      </div>
    )}
  </div>
)
